import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import PageHeader from '../Layout/PageHeader'
import BillingPageStore from './BillingPageStore'
import SessionStore from '../../State/SessionStore'
import { FormatCurrency } from '../../Utils/Localisation/CurrencyFormatter'
import { makeRequest } from '../../Queries/makeRequest'
import { FormatDate } from '../../Utils/Localisation/DateFormatter'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import { trpc } from '@/system/trpc'

export default observer(({ label }) => {
    const subscription = SessionStore.subscription
    const period =
        BillingPageStore.billingPeriod ||
        subscription.subscriptionPeriod ||
        'yearly'
    const [isCreatingCheckoutSession, setIsCreatingCheckoutSession] =
        useState(false)

    if (!subscription) {
        return null
    }

    const handlePeriodChange = (newPeriod) => {
        BillingPageStore.setBillingPeriod(newPeriod)
        // Trigger preview calculation when period changes
        BillingPageStore.calculatePreview()
    }

    const handleSubscribeClick = async () => {
        try {
            setIsCreatingCheckoutSession(true)
            BillingPageStore.setIsSubscribing(true)

            // Track checkout session creation attempt
            posthog.capture('checkout_session_creation_started', {
                period: period,
                organisationId: SessionStore.organisation?.id,
            })

            // Create checkout session using tRPC
            const result = await trpc.legacy.createCheckoutSession.mutate({
                period: period,
            })

            if (result.success && result.sessionUrl) {
                // Track successful session creation
                posthog.capture('checkout_session_created', {
                    sessionId: result.sessionId,
                    period: period,
                    organisationId: SessionStore.organisation?.id,
                })

                // Redirect to Stripe Checkout
                window.location.href = result.sessionUrl
            } else {
                throw new Error('Failed to create checkout session')
            }
        } catch (error) {
            // Track checkout session creation failure
            posthog.capture('checkout_session_creation_failed', {
                error: error.message,
                period: period,
                organisationId: SessionStore.organisation?.id,
            })

            Sentry.captureException(error)
            posthog.captureException(error)

            // Show user-friendly error message
            alert('Failed to create checkout session. Please try again.')

            setIsCreatingCheckoutSession(false)
            BillingPageStore.setIsSubscribing(false)
        }
    }

    return (
        <PageHeader
            heading={
                <div>
                    <div>{label}</div>
                </div>
            }
            leftButtons={[
                <button
                    style={{ marginRight: '1em' }}
                    className="btn btn-default edit-controls__delete-button bg-white/90 m-[0.5em] ml-0 text-[#444] py-2 px-4 text-sm rounded-[6px] border [&:not(:disabled):border-white cursor-pointer[&:not(:disabled):hover]:bg-white/70 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={
                        period === 'yearly' ||
                        BillingPageStore.isUpdatingSubscription ||
                        BillingPageStore.isFetchingPreview ||
                        isCreatingCheckoutSession
                    }
                    onClick={() => handlePeriodChange('yearly')}
                >
                    Yearly (15% discount)
                </button>,
                <button
                    style={{ marginRight: '1em' }}
                    className="btn btn-default edit-controls__delete-button bg-white/90 m-[0.5em] ml-0 text-[#444] py-2 px-4 text-sm rounded-[6px] border [&:not(:disabled):border-white cursor-pointer[&:not(:disabled):hover]:bg-white/70 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={
                        period === 'monthly' ||
                        BillingPageStore.isUpdatingSubscription ||
                        BillingPageStore.isFetchingPreview ||
                        isCreatingCheckoutSession
                    }
                    onClick={() => handlePeriodChange('monthly')}
                >
                    Monthly
                </button>,
                !subscription.hasSubscription ? (
                    <button
                        className="btn btn-primary edit-controls__delete-button"
                        disabled={
                            isCreatingCheckoutSession ||
                            BillingPageStore.isSubscribing
                        }
                        onClick={handleSubscribeClick}
                    >
                        {isCreatingCheckoutSession ||
                        BillingPageStore.isSubscribing
                            ? 'Creating checkout...'
                            : 'Subscribe!'}
                    </button>
                ) : null,
            ]}
            extraContent={
                <div
                    style={{
                        marginTop: '1em',
                        fontSize: '1.5em',
                        fontWeight: '300',
                    }}
                >
                    {`Next Payment: ${FormatCurrency(
                        BillingPageStore.grandTotal,
                        {
                            decimals: 2,
                        }
                    )}`}
                    {SessionStore.subscription.nextPaymentDate
                        ? ` - ${FormatDate(
                              SessionStore.subscription.nextPaymentDate
                          )}`
                        : ''}
                </div>
            }
        />
    )
})
